import type { App, ChainInfo, NetworkId, NetworkInfo } from "@alch/dx-entities";
import { sortBy } from "lodash-es";
import { useCallback, useMemo } from "react";
import NetworkItem from "./NetworkItem.js";

export enum NetworkSort {
  Name = "name",
  Order = "order",
}

export default function useNetworkItems({
  networkInfos,
  chainInfo,
  app,
  sort,
  disabledNetworks,
  disabledInfo,
}: {
  networkInfos: NetworkInfo[];
  chainInfo?: ChainInfo;
  app?: App;
  sort?: NetworkSort;
  disabledNetworks?: NetworkId[];
  disabledInfo?: string;
}) {
  const isNetworkDisabled = useCallback(
    (networkId: NetworkId) =>
      disabledNetworks?.includes(networkId) ||
      (!!app?.networkAllowlist && !app.networkAllowlist.includes(networkId)),
    [disabledNetworks, app?.networkAllowlist],
  );

  return useMemo(
    () =>
      sortBy(
        Object.values(networkInfos),
        (n) => {
          // Move disabled networks to the end
          return isNetworkDisabled(n.id) ? 1 : 0;
        },
        (n) => {
          switch (sort) {
            case NetworkSort.Name:
              return n.name;
            case NetworkSort.Order:
            default:
              // Keep the order as is
              return 0;
          }
        },
      ).map((networkInfo) => {
        const disabled = isNetworkDisabled(networkInfo.id);

        return {
          disabled,
          value: networkInfo.id,
          label: networkInfo.name,
          displayLabel: (
            <NetworkItem
              networkInfo={networkInfo}
              chainInfo={chainInfo}
              disabled={disabled}
              disabledInfo={disabledInfo}
              app={app}
            />
          ),
        };
      }),
    [networkInfos, chainInfo, sort, app, isNetworkDisabled, disabledInfo],
  );
}
